import { useQuery } from "@tanstack/react-query";
import { Clock, ListFilter } from "lucide-react";
import type { AuthMeResponse, BoardField, Label } from "@kandoo/shared";
import { api, type BoardCard } from "../api/client";
import { isDueSoon, isOverdue } from "../util/date";
import { labelColor } from "../util/labelColors";
import { OverflowMenu } from "./OverflowMenu";
import { Avatar } from "./Avatar";

/** A structured due-date filter. Composes any-of within the "Due" dimension. */
export type DueFilter = "overdue" | "due-soon";

/**
 * Client-side board filters. Dimensions compose with AND; selections within a
 * dimension (labels, assignees, due) compose with OR (any-of). Empty in every
 * dimension means "no filter".
 */
export interface BoardFilters {
  /** Only cards assigned to the current user. */
  assignedToMe: boolean;
  /** Any-of these label ids (palette = board.labels). */
  labelIds: string[];
  /** Any-of these assignee user ids. */
  assigneeIds: string[];
  /** Any-of these due states (completed cards are never "due"). */
  due: DueFilter[];
  /**
   * Custom-field matches as "fieldId=value" (a dropdown option id, or "true"
   * for a checked checkbox). Any-of within one field, AND across fields.
   */
  fieldValues: string[];
}

export const EMPTY_FILTERS: BoardFilters = {
  assignedToMe: false,
  labelIds: [],
  assigneeIds: [],
  due: [],
  fieldValues: [],
};

/** Count of active filter selections (drives the trigger badge). */
export function filterCount(f: BoardFilters): number {
  return (
    (f.assignedToMe ? 1 : 0) +
    f.labelIds.length +
    f.assigneeIds.length +
    f.due.length +
    f.fieldValues.length
  );
}

export function filtersActive(f: BoardFilters): boolean {
  return filterCount(f) > 0;
}

/** Does a card satisfy the structured filters? (text query is applied separately) */
export function cardPassesFilters(
  card: BoardCard,
  f: BoardFilters,
  meId: string | null,
): boolean {
  if (f.assignedToMe) {
    if (!meId || !card.assignees.some((a) => a.userId === meId)) return false;
  }
  if (
    f.labelIds.length &&
    !card.labels.some((l) => f.labelIds.includes(l.id))
  )
    return false;
  if (
    f.assigneeIds.length &&
    !card.assignees.some((a) => f.assigneeIds.includes(a.userId))
  )
    return false;
  if (f.due.length) {
    // mirror CardBadges: completed cards are excluded; "due soon" excludes overdue
    const overdue =
      !!card.dueDate && !card.completed && isOverdue(card.dueDate);
    const dueSoon =
      !!card.dueDate && !card.completed && !overdue && isDueSoon(card.dueDate);
    const ok =
      (f.due.includes("overdue") && overdue) ||
      (f.due.includes("due-soon") && dueSoon);
    if (!ok) return false;
  }
  if (f.fieldValues.length) {
    const wanted = new Map<string, Set<string>>();
    for (const key of f.fieldValues) {
      const [fieldId, value] = key.split("=");
      wanted.set(fieldId, (wanted.get(fieldId) ?? new Set()).add(value));
    }
    for (const [fieldId, values] of wanted) {
      const v = card.fieldValues?.[fieldId];
      if (v === undefined || !values.has(String(v))) return false;
    }
  }
  return true;
}

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value)
    ? list.filter((x) => x !== value)
    : [...list, value];
}

interface FilterRowProps {
  checked: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function FilterRow({ checked, onClick, children }: FilterRowProps) {
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={checked}
      onClick={onClick}
      className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50"
    >
      <input
        type="checkbox"
        checked={checked}
        readOnly
        className="pointer-events-none"
      />
      {children}
    </button>
  );
}

interface BoardFiltersProps {
  boardId: string;
  labels: Label[];
  /** Board custom fields; dropdown and checkbox ones become filter sections. */
  fields: BoardField[];
  me: AuthMeResponse | null;
  filters: BoardFilters;
  onChange: (next: BoardFilters) => void;
}

/** Header "Filter" button + dropdown of structured board filters. */
export function BoardFilters({
  boardId,
  labels,
  fields,
  me,
  filters,
  onChange,
}: BoardFiltersProps) {
  const filterable = [...fields]
    .filter((f) => f.type === "select" || f.type === "checkbox")
    .sort((a, b) => a.position - b.position);
  const toggleField = (key: string) =>
    onChange({ ...filters, fieldValues: toggle(filters.fieldValues, key) });
  const membersQ = useQuery({
    queryKey: ["board-members", boardId],
    queryFn: () => api.members.list(boardId),
    enabled: !!boardId,
  });
  const members = membersQ.data ?? [];
  const active = filterCount(filters);

  return (
    <OverflowMenu
      ariaLabel="Filter cards"
      align="right"
      triggerClassName={`relative rounded px-2 py-1.5 hover:bg-slate-200 ${
        active
          ? "bg-blue-100 text-blue-700"
          : "bg-slate-100 text-slate-700"
      }`}
      trigger={
        <>
          <ListFilter size={16} />
          {active > 0 && (
            <span
              aria-hidden
              className="absolute -right-1 -top-1 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-semibold leading-none text-white"
            >
              {active}
            </span>
          )}
        </>
      }
    >
      <div className="max-h-96 w-64 overflow-y-auto">
        <div className="flex items-center justify-between px-3 py-2">
          <span className="text-xs font-medium text-slate-500">Filters</span>
          {active > 0 && (
            <button
              type="button"
              onClick={() => onChange(EMPTY_FILTERS)}
              className="text-xs text-blue-600 hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>

        {me && (
          <>
            <div className="my-1 border-t border-slate-100" />
            <FilterRow
              checked={filters.assignedToMe}
              onClick={() =>
                onChange({ ...filters, assignedToMe: !filters.assignedToMe })
              }
            >
              <Avatar userId={me.id} name={me.displayName || me.username} />
              <span className="flex-1">Assigned to me</span>
            </FilterRow>
          </>
        )}

        <div className="my-1 border-t border-slate-100" />
        <div className="px-3 pb-0.5 pt-1 text-xs text-slate-400">Due</div>
        <FilterRow
          checked={filters.due.includes("overdue")}
          onClick={() =>
            onChange({ ...filters, due: toggle(filters.due, "overdue") })
          }
        >
          <Clock size={14} className="text-slate-400" />
          <span className="flex-1">Overdue</span>
        </FilterRow>
        <FilterRow
          checked={filters.due.includes("due-soon")}
          onClick={() =>
            onChange({ ...filters, due: toggle(filters.due, "due-soon") })
          }
        >
          <Clock size={14} className="text-slate-400" />
          <span className="flex-1">Due soon</span>
        </FilterRow>

        {labels.length > 0 && (
          <>
            <div className="my-1 border-t border-slate-100" />
            <div className="px-3 pb-0.5 pt-1 text-xs text-slate-400">
              Labels
            </div>
            {labels.map((l) => (
              <FilterRow
                key={l.id}
                checked={filters.labelIds.includes(l.id)}
                onClick={() =>
                  onChange({
                    ...filters,
                    labelIds: toggle(filters.labelIds, l.id),
                  })
                }
              >
                <span
                  className={`h-3 w-6 shrink-0 rounded ${labelColor(l.color).swatch}`}
                />
                <span className="flex-1 truncate">
                  {l.name || labelColor(l.color).label}
                </span>
              </FilterRow>
            ))}
          </>
        )}

        {filterable.map((f) => (
          <div key={f.id}>
            <div className="my-1 border-t border-slate-100" />
            <div className="truncate px-3 pb-0.5 pt-1 text-xs text-slate-400">{f.name}</div>
            {f.type === "checkbox" ? (
              <FilterRow
                checked={filters.fieldValues.includes(`${f.id}=true`)}
                onClick={() => toggleField(`${f.id}=true`)}
              >
                <span className="flex-1">Checked</span>
              </FilterRow>
            ) : (
              f.options.map((o) => (
                <FilterRow
                  key={o.id}
                  checked={filters.fieldValues.includes(`${f.id}=${o.id}`)}
                  onClick={() => toggleField(`${f.id}=${o.id}`)}
                >
                  <span className={`h-3 w-3 shrink-0 rounded-full ${labelColor(o.color).swatch}`} />
                  <span className="flex-1 truncate">{o.name}</span>
                </FilterRow>
              ))
            )}
          </div>
        ))}

        {members.length > 0 && (
          <>
            <div className="my-1 border-t border-slate-100" />
            <div className="px-3 pb-0.5 pt-1 text-xs text-slate-400">
              Assignees
            </div>
            {members.map((m) => (
              <FilterRow
                key={m.userId}
                checked={filters.assigneeIds.includes(m.userId)}
                onClick={() =>
                  onChange({
                    ...filters,
                    assigneeIds: toggle(filters.assigneeIds, m.userId),
                  })
                }
              >
                <Avatar
                  userId={m.userId}
                  name={m.displayName || m.username}
                />
                <span className="flex-1 truncate">
                  {m.displayName || m.username}
                </span>
                <span className="text-xs text-slate-400">@{m.username}</span>
              </FilterRow>
            ))}
          </>
        )}
      </div>
    </OverflowMenu>
  );
}
